<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import AgentAssignmentPolicyForm from 'dashboard/routes/dashboard/settings/assignmentPolicy/pages/components/AgentAssignmentPolicyForm.vue';
import {
  ROUND_ROBIN,
  EARLIEST_CREATED,
  DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

const props = defineProps({
  teamId: { type: [Number, String], required: true },
});

const store = useStore();
const { t } = useI18n();
const policy = ref(null);
const isLoading = ref(false);
const defaults = {
  assignmentOrder: ROUND_ROBIN,
  conversationPriority: EARLIEST_CREATED,
  fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
};
const formData = computed(() => ({ ...defaults, ...policy.value }));

const load = async () => {
  isLoading.value = true;
  try {
    policy.value = await store.dispatch('assignmentPolicies/getTeamPolicy', {
      teamId: props.teamId,
    });
  } catch {
    useAlert(t('TEAMS_SETTINGS.DISTRIBUTION.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async values => {
  isLoading.value = true;
  try {
    policy.value = await store.dispatch(
      'assignmentPolicies/configureTeamPolicy',
      { teamId: props.teamId, policy: values }
    );
    useAlert(t('TEAMS_SETTINGS.DISTRIBUTION.SAVE_SUCCESS'));
  } catch {
    useAlert(t('TEAMS_SETTINGS.DISTRIBUTION.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <SettingsAccordion
    :title="$t('TEAMS_SETTINGS.DISTRIBUTION.TITLE')"
    class="mt-6"
  >
    <div class="px-4 py-3 text-sm text-n-slate-11">
      {{ $t('TEAMS_SETTINGS.DISTRIBUTION.DESCRIPTION') }}
    </div>
    <AgentAssignmentPolicyForm
      :initial-data="formData"
      :is-loading="isLoading"
      @submit="save"
    />
  </SettingsAccordion>
</template>
